// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * What you import is not always what you install.
 *
 * `import cv2` comes from `opencv-python`; `import yaml` comes from `PyYAML`;
 * `from PIL import Image` comes from `pillow`. A checker that looks up the
 * import name goes to PyPI, is told `cv2` does not exist, and reports the most
 * widely used computer-vision library in the world as a hallucination. That
 * single false positive would destroy the tool's credibility in its first
 * minute, which is why this table exists at all.
 *
 * TWO RULES GOVERN IT, AND BOTH ARE ABOUT NOT LYING:
 *
 * 1. A MAPPING IS EVIDENCE, NEVER AN ASSERTION. Every substitution is shown in
 *    the interface with the import name it came from, and every one can be
 *    overridden. This table is hand-written and therefore wrong somewhere; the
 *    interface is built so that being wrong here is a visible, correctable
 *    inconvenience rather than a confident false verdict.
 *
 * 2. WHERE THE ANSWER IS GENUINELY AMBIGUOUS, SAY SO RATHER THAN PICKING. The
 *    value is an array. `import psycopg2` is satisfied by `psycopg2`,
 *    `psycopg2-binary` and `psycopg2cffi`, all real, all common, and nothing in
 *    the source tells you which one is installed. The tool offers all three and
 *    makes you choose instead of quietly pinning one.
 *
 * v1 ships this bundled table only. Resolving an import name properly means
 * reading the `top_level.txt` / `RECORD` of every candidate wheel, which is a
 * live lookup against files this tool would have to download — deferred.
 */

export interface Mapping {
  /** The distribution name(s) that provide this import. */
  readonly to: readonly string[];
  /** Shown to the user beside the substitution. */
  readonly note?: string;
}

/**
 * Python import name → PyPI distribution name(s).
 *
 * Keyed on the TOP-LEVEL import only: `google.protobuf` is looked up as
 * `google`, then the full dotted path is tried first so the more specific
 * entry wins.
 */
export const PYTHON_IMPORTS: Readonly<Record<string, Mapping>> = {
  /* the classics — the ones that would otherwise be reported as fabrications */
  cv2: { to: ['opencv-python'], note: 'also opencv-python-headless / opencv-contrib-python' },
  PIL: { to: ['pillow'] },
  yaml: { to: ['PyYAML'] },
  sklearn: { to: ['scikit-learn'] },
  skimage: { to: ['scikit-image'] },
  bs4: { to: ['beautifulsoup4'] },
  dateutil: { to: ['python-dateutil'] },
  dotenv: { to: ['python-dotenv'] },
  jwt: { to: ['PyJWT'] },
  serial: { to: ['pyserial'] },
  usb: { to: ['pyusb'] },
  OpenSSL: { to: ['pyOpenSSL'] },
  Crypto: { to: ['pycryptodome'], note: 'the abandoned `pycrypto` also provided this; prefer pycryptodome' },
  Cryptodome: { to: ['pycryptodomex'] },
  attr: { to: ['attrs'] },
  attrs: { to: ['attrs'] },
  git: { to: ['GitPython'] },
  docx: { to: ['python-docx'] },
  pptx: { to: ['python-pptx'] },
  fitz: { to: ['PyMuPDF'] },
  Bio: { to: ['biopython'] },
  psycopg2: { to: ['psycopg2', 'psycopg2-binary'], note: 'binary wheel or source build — pick the one you install' },
  MySQLdb: { to: ['mysqlclient'] },
  pymysql: { to: ['PyMySQL'] },
  zmq: { to: ['pyzmq'] },
  gi: { to: ['PyGObject'] },
  cairo: { to: ['pycairo'] },
  wx: { to: ['wxPython'] },
  OpenGL: { to: ['PyOpenGL'] },
  magic: { to: ['python-magic'] },
  Xlib: { to: ['python-xlib'] },
  win32com: { to: ['pywin32'] },
  win32api: { to: ['pywin32'] },
  win32con: { to: ['pywin32'] },
  pythoncom: { to: ['pywin32'] },
  pkg_resources: { to: ['setuptools'] },
  mpl_toolkits: { to: ['matplotlib'] },
  IPython: { to: ['ipython'] },
  nacl: { to: ['PyNaCl'] },
  jose: { to: ['python-jose'] },
  Levenshtein: { to: ['python-Levenshtein', 'Levenshtein'] },
  igraph: { to: ['python-igraph'] },
  pyzbar: { to: ['pyzbar'] },
  barcode: { to: ['python-barcode'] },
  telegram: { to: ['python-telegram-bot'] },
  discord: { to: ['discord.py'] },
  github: { to: ['PyGithub'] },
  gitlab: { to: ['python-gitlab'] },
  googleapiclient: { to: ['google-api-python-client'] },
  google: { to: ['protobuf', 'google-api-core'], note: 'a namespace package — google.protobuf is protobuf, google.auth is google-auth' },
  'google.protobuf': { to: ['protobuf'] },
  'google.auth': { to: ['google-auth'] },
  'google.cloud': { to: [], note: 'a namespace package: the distribution is google-cloud-<service>, e.g. google-cloud-storage' },
  grpc: { to: ['grpcio'] },
  grpc_tools: { to: ['grpcio-tools'] },
  kafka: { to: ['kafka-python'] },
  pdfminer: { to: ['pdfminer.six'] },
  newspaper: { to: ['newspaper3k'] },
  readability: { to: ['readability-lxml'] },
  speech_recognition: { to: ['SpeechRecognition'] },
  pyaudio: { to: ['PyAudio'] },
  skvideo: { to: ['scikit-video'] },
  face_recognition: { to: ['face-recognition'] },
  tflite_runtime: { to: ['tflite-runtime'] },
  rest_framework: { to: ['djangorestframework'] },
  faiss: { to: ['faiss-cpu', 'faiss-gpu'] },
  pinecone: { to: ['pinecone-client', 'pinecone'] },
  qdrant_client: { to: ['qdrant-client'] },
  sentence_transformers: { to: ['sentence-transformers'] },
  huggingface_hub: { to: ['huggingface-hub'] },
  slack_sdk: { to: ['slack-sdk'] },
  notion_client: { to: ['notion-client'] },
  prometheus_client: { to: ['prometheus-client'] },
  sentry_sdk: { to: ['sentry-sdk'] },
  opentelemetry: { to: ['opentelemetry-api'], note: 'plus opentelemetry-sdk and an exporter package' },
  importlib_metadata: { to: ['importlib-metadata'] },
  typing_extensions: { to: ['typing-extensions'] },
  charset_normalizer: { to: ['charset-normalizer'] },
  setuptools_scm: { to: ['setuptools-scm'] },
  pre_commit: { to: ['pre-commit'] },
  pydantic_settings: { to: ['pydantic-settings'] },
  email_validator: { to: ['email-validator'] },
  requests_oauthlib: { to: ['requests-oauthlib'] },
  factory: { to: ['factory_boy'] },
  faker: { to: ['Faker'] },
  cx_Oracle: { to: ['cx-Oracle'] },
  tortoise: { to: ['tortoise-orm'] },
  jinja2: { to: ['Jinja2'] },
  markdown: { to: ['Markdown'] },
  babel: { to: ['Babel'] },
  flask: { to: ['Flask'] },
  django: { to: ['Django'] },
  sqlalchemy: { to: ['SQLAlchemy'] },
  werkzeug: { to: ['Werkzeug'] },
  scrapy: { to: ['Scrapy'] },
  shapely: { to: ['Shapely'] },
  fiona: { to: ['Fiona'] },
  pyautogui: { to: ['PyAutoGUI'] },
  apscheduler: { to: ['APScheduler'] },
  unidecode: { to: ['Unidecode'] },
  xlsxwriter: { to: ['XlsxWriter'] },
  configargparse: { to: ['ConfigArgParse'] },
  authlib: { to: ['Authlib'] },
  kivy: { to: ['Kivy'] },
  netCDF4: { to: ['netCDF4'] },
  prompt_toolkit: { to: ['prompt-toolkit'] },
  ruamel: { to: ['ruamel.yaml'] },
};

/**
 * The import names above that are ALSO valid distribution names, so a lookup
 * would have succeeded anyway and the substitution must not be shown as a
 * correction. Kept explicit rather than derived, because "the mapping was a
 * no-op" and "the mapping was needed" read very differently in the interface.
 */
const IDENTITY_LIKE = new Set(['attrs', 'psycopg2', 'pyzbar', 'netCDF4']);

export interface Resolution {
  /** What appeared in the source. */
  readonly importName: string;
  /** What to look up. Empty when the tool refuses to guess. */
  readonly candidates: readonly string[];
  /** Non-null when a substitution was made, for display beside the row. */
  readonly note: string | null;
  readonly substituted: boolean;
}

/** Resolve a Python import to the distribution name(s) to check. */
export function resolvePythonImport(raw: string): Resolution {
  const importName = raw.trim();
  const top = importName.split('.')[0];
  const twoLevel = importName.split('.').slice(0, 2).join('.');

  for (const key of [importName, twoLevel, top]) {
    const hit = PYTHON_IMPORTS[key];
    if (!hit) continue;
    return {
      importName,
      candidates: hit.to,
      note: hit.note ?? (hit.to.length ? `\`${key}\` is provided by ${hit.to.join(' or ')}` : null),
      substituted: !IDENTITY_LIKE.has(key) && (hit.to.length !== 1 || hit.to[0] !== key),
    };
  }
  return { importName, candidates: [top], note: null, substituted: false };
}

/**
 * The npm package that owns a specifier.
 *
 * This one is a rule rather than a table, because npm's own resolution is a
 * rule: `@scope/name/sub/path` belongs to `@scope/name`, and `name/sub/path`
 * to `name`. Getting it wrong means asking the registry about
 * `date-fns/format`, which 404s, and reporting date-fns as a hallucination.
 */
export function resolveNpmSpecifier(raw: string): string | null {
  const spec = raw.trim();
  if (!spec) return null;
  // Relative, absolute, protocol and builtin specifiers are not packages.
  if (/^[./]/.test(spec)) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(spec)) return null;
  const parts = spec.split('/');
  if (spec.startsWith('@')) {
    if (parts.length < 2 || !parts[1]) return null;
    return `${parts[0]}/${parts[1]}`;
  }
  return parts[0] || null;
}
